import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Search, Download, Printer, ChevronLeft, Send, AlertCircle, CheckCircle, Clock } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Email Logs List Page
 */
export default function KyleRiseEmailLogs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchLogs(); }, [page, statusFilter, typeFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/email-logs?page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (typeFilter !== "all") url += `&type=${typeFilter}`;
      if (search) url += `&recipient=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      setLogs(res.data.email_logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    const headers = ["Email ID", "Type", "Recipient", "Subject", "Status", "Payment Ref", "Sent At"];
    const rows = logs.map(l => [l.email_id, l.type, l.recipient, l.subject, l.status, l.payment_ref || "", l.sent_at ? new Date(l.sent_at).toLocaleString() : ""]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `email-logs-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "pending": case "logged": return <Clock className="w-4 h-4 text-yellow-500" />;
      case "failed": return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-email-logs">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/kyle-rise")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Email & Receipt Logs</h1><p className="text-sm text-gray-500">{total} emails</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchLogs()} placeholder="Search by recipient..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Types</option>
            <option value="receipt">Receipt</option>
            <option value="interest_notification">Interest Notification</option>
            <option value="pass_confirmation">Pass Confirmation</option>
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="sent">Sent</option>
            <option value="pending">Pending</option>
            <option value="logged">Logged</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Recipient</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment Ref</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sent At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>)
              : logs.length === 0 ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No email logs found</td></tr>)
              : logs.map((log) => (
                <tr key={log.email_id} onClick={() => navigate(`/admin/kyle-rise/email-logs/${log.email_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className="text-sm text-gray-500">{log.email_id}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{log.recipient}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{log.subject}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">{log.type}</span></td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${log.status === "sent" ? "bg-green-100 text-green-700" : log.status === "pending" || log.status === "logged" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>{log.status}</span></td>
                  <td className="px-4 py-3">
                    {log.payment_ref && (
                      <span onClick={(e) => { e.stopPropagation(); navigate(`/admin/kyle-rise/payments/${log.payment_ref}`); }} className="text-sm text-blue-600 hover:underline cursor-pointer">{log.payment_ref}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{log.sent_at ? new Date(log.sent_at).toLocaleString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
}
